import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  forecastModelTypeId,
  type ForecastModelTypeId,
} from './ForecastModelType.js'
import {
  labelSortType,
  type default as LabelSortType,
} from './LabelSortType.js'
import {
  lengthUnitType,
  type default as LengthUnitType,
} from './LengthUnitType.js'
import { siteLayoutId, type SiteLayoutId } from './SiteLayout.js'
import { sitePageTypeId, type SitePageTypeId } from './SitePageType.js'
import {
  siteStatusType,
  type default as SiteStatusType,
} from './SiteStatusType.js'
import { tempUnitType, type default as TempUnitType } from './TempUnitType.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.legacy_site_ */
export type LegacySiteId = number & { __brand: 'LegacySiteId' }

/** Represents the table public.legacy_site_ */
export default interface LegacySiteTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<LegacySiteId, LegacySiteId | undefined, LegacySiteId>

  /** Database type: pg_catalog.text */
  siteName: ColumnType<string, string, string>

  /** Database type: pg_catalog.bool */
  visible: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: public.site_status_type_ */
  status: ColumnType<
    SiteStatusType | null,
    SiteStatusType | null,
    SiteStatusType | null
  >

  /** Database type: pg_catalog.int4 */
  sortOrder: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.bool */
  includesArchivedData: ColumnType<
    boolean | null,
    boolean | null,
    boolean | null
  >

  /** Database type: pg_catalog.text */
  internalNotes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  customerNotes: ColumnType<string | null, string | null, string | null>

  /** Database type: public.length_unit_type_ */
  lengthUnits: ColumnType<
    LengthUnitType,
    LengthUnitType | undefined,
    LengthUnitType
  >

  /** Database type: public.temp_unit_type_ */
  tempUnits: ColumnType<TempUnitType, TempUnitType | undefined, TempUnitType>

  /** Database type: pg_catalog.numeric */
  geoLat: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  geoLon: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  geoElev: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.bool */
  plotSnow: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.numeric */
  sonicHeight: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.bool */
  labelSettingShowCableSerial: ColumnType<
    boolean | null,
    boolean | null,
    boolean | null
  >

  /** Database type: public.label_sort_type_ */
  labelSettingSortBy: ColumnType<
    LabelSortType | null,
    LabelSortType | null,
    LabelSortType | null
  >

  /** Database type: pg_catalog.int4 */
  readingsVersion: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.bool */
  useRecordDate: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.bool */
  showSdmLines: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.bool */
  showIdmLines: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.bool */
  showForecast: ColumnType<boolean, boolean | undefined, boolean>

  /**
   * Database type: pg_catalog.text
   * ModelFactory model SLUG that serves this site's forecast (FK to forecast_model_type_), NULL when unassigned (#4169). Despite the column name this is NOT the router's model_type argument -- see forecast_model_type_ for why the two vocabularies differ. Set ONLY by the GLOBAL_ADMIN approve endpoint -- PUT /site/:id and POST /site strip it at both the validation and handler layers.
   */
  forecastModelType: ColumnType<
    ForecastModelTypeId | null,
    ForecastModelTypeId | null,
    ForecastModelTypeId | null
  >

  /** Database type: pg_catalog.text */
  pageType: ColumnType<
    SitePageTypeId,
    SitePageTypeId | undefined,
    SitePageTypeId
  >

  /** Database type: pg_catalog.text */
  layout: ColumnType<SiteLayoutId, SiteLayoutId | undefined, SiteLayoutId>

  /**
   * Database type: pg_catalog.bool
   * Reverses the assumed order of thermal sensors that have no depth on the borehole dashboard (default false: channel 1 is the shallowest). Set only by global editors through PUT /site/:id. Superseded per sensor by a stored sensor_display_.depth_m (#4656).
   */
  flipThermalOrder: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.numeric */
  radarReferenceHeight: ColumnType<number | null, number | null, number | null>
  /**
   * Database type: pg_catalog.bool
   * UNUSED since #4658 (2026-10-26): nothing reads or writes it; every site shows its full vendor channel set (all loggers, soft sensors, B units, resistance, cleaned names). Kept only because the deploy migration role cannot remove columns; an owner-applied migration removes it (#4658).
   */
  enhancedVendorDisplay: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.int4 */
  lineGapSize: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  embeddedUuid: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  archivedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.text */
  wivMeasuringPointId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  wivBegin: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  wivEnd: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>
}

export type LegacySite = Selectable<LegacySiteTable>

export type LegacySiteInitializer = Insertable<LegacySiteTable>

export type LegacySiteMutator = Updateable<LegacySiteTable>

export const legacySiteId = z.number() as unknown as z.Schema<LegacySiteId>

export const legacySite = z.object({
  id: legacySiteId,
  siteName: z.string(),
  visible: z.boolean().nullable().optional(),
  status: siteStatusType.nullable().optional(),
  sortOrder: z.number().nullable().optional(),
  includesArchivedData: z.boolean().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  customerNotes: z.string().nullable().optional(),
  lengthUnits: lengthUnitType,
  tempUnits: tempUnitType,
  geoLat: z.number().nullable().optional(),
  geoLon: z.number().nullable().optional(),
  geoElev: z.number().nullable().optional(),
  plotSnow: z.boolean().nullable().optional(),
  sonicHeight: z.number().nullable().optional(),
  labelSettingShowCableSerial: z.boolean().nullable().optional(),
  labelSettingSortBy: labelSortType.nullable().optional(),
  readingsVersion: z.number().nullable().optional(),
  useRecordDate: z.boolean().nullable().optional(),
  showSdmLines: z.boolean().nullable().optional(),
  showIdmLines: z.boolean().nullable().optional(),
  showForecast: z.boolean(),
  forecastModelType: forecastModelTypeId.nullable().optional(),
  pageType: sitePageTypeId,
  layout: siteLayoutId,
  flipThermalOrder: z.boolean(),
  radarReferenceHeight: z.number().nullable().optional(),
  enhancedVendorDisplay: z.boolean(),
  lineGapSize: z.number().nullable().optional(),
  embeddedUuid: z.string().nullable().optional(),
  archivedAt: z.coerce.date().nullable().optional(),
  wivMeasuringPointId: z.string().nullable().optional(),
  wivBegin: z.coerce.date().nullable().optional(),
  wivEnd: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
}) as unknown as z.Schema<LegacySite>

export const legacySiteInitializer = z.object({
  id: legacySiteId.optional(),
  siteName: z.string(),
  visible: z.boolean().optional().nullable(),
  status: siteStatusType.optional().nullable(),
  sortOrder: z.number().optional().nullable(),
  includesArchivedData: z.boolean().optional().nullable(),
  internalNotes: z.string().optional().nullable(),
  customerNotes: z.string().optional().nullable(),
  lengthUnits: lengthUnitType.optional(),
  tempUnits: tempUnitType.optional(),
  geoLat: z.number().optional().nullable(),
  geoLon: z.number().optional().nullable(),
  geoElev: z.number().optional().nullable(),
  plotSnow: z.boolean().optional().nullable(),
  sonicHeight: z.number().optional().nullable(),
  labelSettingShowCableSerial: z.boolean().optional().nullable(),
  labelSettingSortBy: labelSortType.optional().nullable(),
  readingsVersion: z.number().optional().nullable(),
  useRecordDate: z.boolean().optional().nullable(),
  showSdmLines: z.boolean().optional().nullable(),
  showIdmLines: z.boolean().optional().nullable(),
  showForecast: z.boolean().optional(),
  forecastModelType: forecastModelTypeId.optional().nullable(),
  pageType: sitePageTypeId.optional(),
  layout: siteLayoutId.optional(),
  flipThermalOrder: z.boolean().optional(),
  radarReferenceHeight: z.number().optional().nullable(),
  enhancedVendorDisplay: z.boolean().optional(),
  lineGapSize: z.number().optional().nullable(),
  embeddedUuid: z.string().optional().nullable(),
  archivedAt: z.coerce.date().optional().nullable(),
  wivMeasuringPointId: z.string().optional().nullable(),
  wivBegin: z.coerce.date().optional().nullable(),
  wivEnd: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteInitializer>

export const legacySiteMutator = z.object({
  id: legacySiteId.optional(),
  siteName: z.string().optional(),
  visible: z.boolean().optional().nullable(),
  status: siteStatusType.optional().nullable(),
  sortOrder: z.number().optional().nullable(),
  includesArchivedData: z.boolean().optional().nullable(),
  internalNotes: z.string().optional().nullable(),
  customerNotes: z.string().optional().nullable(),
  lengthUnits: lengthUnitType.optional(),
  tempUnits: tempUnitType.optional(),
  geoLat: z.number().optional().nullable(),
  geoLon: z.number().optional().nullable(),
  geoElev: z.number().optional().nullable(),
  plotSnow: z.boolean().optional().nullable(),
  sonicHeight: z.number().optional().nullable(),
  labelSettingShowCableSerial: z.boolean().optional().nullable(),
  labelSettingSortBy: labelSortType.optional().nullable(),
  readingsVersion: z.number().optional().nullable(),
  useRecordDate: z.boolean().optional().nullable(),
  showSdmLines: z.boolean().optional().nullable(),
  showIdmLines: z.boolean().optional().nullable(),
  showForecast: z.boolean().optional(),
  forecastModelType: forecastModelTypeId.optional().nullable(),
  pageType: sitePageTypeId.optional(),
  layout: siteLayoutId.optional(),
  flipThermalOrder: z.boolean().optional(),
  radarReferenceHeight: z.number().optional().nullable(),
  enhancedVendorDisplay: z.boolean().optional(),
  lineGapSize: z.number().optional().nullable(),
  embeddedUuid: z.string().optional().nullable(),
  archivedAt: z.coerce.date().optional().nullable(),
  wivMeasuringPointId: z.string().optional().nullable(),
  wivBegin: z.coerce.date().optional().nullable(),
  wivEnd: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteMutator>
